import { CommonModule, NgClass } from '@angular/common'
import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { ToastrService } from '@app/services/toastr.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Observable, Subject } from 'rxjs'
import { finalize, map, takeUntil } from 'rxjs/operators'
import { BADGE_TYPES } from '../../../enums/badge.enum'
import { BADGES } from '../../../models/badges.model'
import { BadgeEditorService, updateBadgePayload, updateBadgeResponse } from '../../../services/badge-editor.service'
import { Badge, TemplateLayout } from '../../../types/badge.type'
@Component({
  selector: 'app-badge-template-selection',
  templateUrl: './badge-template-selection.component.html',
  styleUrls: ['./badge-template-selection.component.scss'],
  standalone: true,
  imports: [CommonModule, ModalComponent, SafeDomPipe, ReactiveFormsModule, FormsModule]
})
export class BadgeTemplateSelectionComponent {
  @Input() public badgeForm!: FormGroup
  @Output() public onBadgeSelected: EventEmitter<TemplateLayout> = new EventEmitter<TemplateLayout>()
  @Output() public onCancel: EventEmitter<void> = new EventEmitter<void>()

  public badges = BADGES
  public customBadgeForm: FormGroup = new FormGroup({
    width: new FormControl(null, [Validators.required, Validators.min(1), this._validateBadgeDimensions]),
    height: new FormControl(null, [Validators.required, Validators.min(1), this._validateBadgeDimensions])
  })
  public isSaving: boolean = false
  private personaId!: number
  private eventId!: number
  private readonly _destroyRef = inject(DestroyRef)

  constructor(private _ModalService: ModalService, private _BadgeService: BadgeEditorService, private _ActivatedRoute: ActivatedRoute, private _ToastrService: ToastrService) {}

  ngOnInit(): void {
    this.eventId = +this._ActivatedRoute.snapshot.params['eventId']
    this.personaId = +this._ActivatedRoute.snapshot.params['personaId']
  }

  /**
   * @method _validateBadgeDimensions
   * @description Validate the badge dimensions and check if the width and height are less than 300mm
   * @param control FormControl
   * @returns ValidationErrors | null
   */
  private _validateBadgeDimensions(control: FormControl): ValidationErrors | null {
    const parent = control.parent
    if (!parent) return null
    const controlName = Object.entries(parent.controls).find(([_, value]) => value === control)?.[0]
    if (control.value > 300) return { maxBadgeWidth: `Maximum badge ${controlName} is 300mm.` }
    if (controlName === 'width' && control.value < 40) return { minBadgeWidth: 'Minimum badge width is 40mm.' }
    return null
  }

  /**
   * @method selectTemplate
   * @description Selects a badge template and opens the modal if badge is custom badge
   * @param badge Badge
   * @returns void
   */
  public selectTemplate(badge: Badge): void {
    if (!badge.isAvailable) return
    switch (badge.badge_type) {
      case BADGE_TYPES.CUSTOM_BADGE: {
        if (!this.isModalOpen()) {
          this.toggleModal()
          setTimeout(() => {
            ;(<HTMLInputElement>document.querySelector('#badge-width-input'))?.focus()
          })
        }
        break
      }
      case BADGE_TYPES.FIVE_INCH: {
        const payload: updateBadgePayload = {
          background_preprinted: false,
          form_id: this.badgeForm.get('id')?.value,
          badge_front_html_content: '',
          reset: true
        }
        this.saveBadge(payload, 'COMMON')
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: (response) => {
              //ResetBadgeBackground
              this._BadgeService.frontFixedElement = response.badge.front_fixed_element
              this.badgeForm.get('badge_front_html_content')?.setValue(response.badge.badge_front_html_content)
              const parser = new DOMParser()
              const badgeDocument = parser.parseFromString(response.badge.badge_front_html_content, 'text/html')
              const badge = badgeDocument.getElementById('badge') as HTMLDivElement
              const template = {
                type: BADGE_TYPES.FIVE_INCH,
                width: +(badge.getAttribute('data-width') || 0),
                height: +(badge.getAttribute('data-height') || 0)
              }
              this._BadgeService.selectedTemplate = template
              this.onBadgeSelected.emit(template)
            }
          })
        break
      }
    }
  }

  /**
   * @method saveBadge
   * @description Handle save badge API with loading
   * @param payload accepts BadgeTemplateResponse
   * @param reset accepts boolean
   * @param template accepts string
   * @returns Observable<Response<BadgeResponse>>
   */
  saveBadge(payload: updateBadgePayload, template?: string): Observable<updateBadgeResponse> {
    this.isSaving = true
    return this._BadgeService.saveBadge(payload, template).pipe(
      map(
        (response) => {
          this._ToastrService.success('Success', 'Badge has been saved successfully')
          return response
        },
        finalize(() => (this.isSaving = false))
      )
    )
  }

  /**
   * @method searchControl
   * @description Search for a control in the form group by the control name
   * @param controlName string
   * @returns
   */
  public searchControl(controlName: string): FormControl {
    return this._BadgeService.searchControl(this.customBadgeForm, controlName) as FormControl
  }

  /**
   * @method saveModal
   * @description Save the modal and emit the badge type to the parent component
   * @param event MouseEvent
   * @returns void
   */
  public saveModal(event: MouseEvent): void {
    event.preventDefault()
    this.customBadgeForm.markAllAsTouched()
    if (this.customBadgeForm.invalid) return
    const template = {
      type: BADGE_TYPES.CUSTOM_BADGE,
      width: +this.searchControl('width').value,
      height: +this.searchControl('height').value
    }
    const payload: updateBadgePayload = {
      background_preprinted: false,
      badge_front_html_content: this._setBadgeHTML(template),
      reset: false,
      form_id: this.badgeForm.get('id')?.value
    }
    this.saveBadge(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.toggleModal()
          //ResetBadgeBackground
          this._BadgeService.frontFixedElement = response.badge.front_fixed_element
          this.badgeForm.get('badge_front_html_content')?.setValue(response.badge.badge_front_html_content)
          this.badgeForm.get('background_preprinted')?.setValue(response.badge.background_preprinted)
          this._BadgeService.selectedTemplate = template
          this.onBadgeSelected.emit(template)
        }
      })
  }

  /**
   * @method toggleModal
   * @description Toggle the modal by the modal id
   * @param reset boolean
   * @returns void
   */
  public toggleModal(reset: boolean = false): void {
    if (reset) this.customBadgeForm.reset()
    this._ModalService.toggleModal('custom-badge-modal')
  }

  /**
   * @method isModalOpen
   * @description Check if the modal is open or not by the modal id
   * @returns boolean
   */
  public isModalOpen(): boolean {
    return this._ModalService.isModalOpen('custom-badge-modal')
  }

  /**
   * @method _setBadgeHTML
   * @description Return empty badge HTML width specific aspect ratio
   * @param template accepts TemplateLayout
   * @returns string
   */
  private _setBadgeHTML(template: TemplateLayout): string {
    return `<div id="badge" data-width="${template.width}" data-height="${template.height}" data-badgetype="5" style="background-color: rgb(255, 255, 255); width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; user-select: none; background-size: cover; background-repeat: no-repeat;"></div>`
  }

  /**
   * @description Cancel the template selection
   */
  public cancelTemplateSelection(): void {
    this.onCancel.emit()
  }
}
